import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { ArrowRight, Sparkles, Send, Play, Command } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import InteractiveGlobe from './InteractiveGlobe';
import { GoogleIcon } from './BrandIcons';
import { OfficialLogo } from './OfficialLogos';

const PLACEHOLDERS = [
  'Build a Stripe-powered marketplace for indie musicians…',
  'Design a Lovable-style holiday planner with PDF exports…',
  'Ship a CRM for small law firms with e-signatures…',
  'A flight itinerary maker for travel agents…',
  'A dark-mode analytics dashboard for TikTok creators…',
  'A shopify-alternative for artisan coffee brands…',
];

/**
 * OPTION D — "World Class" Hero
 * Void-black. React Globe GL background. Massive glassmorphism prompt box.
 * Marquee of AI models. Framer Motion entrance. Chromatic accents.
 */
export default function HeroWorldClass({ onSignIn }) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [phIdx, setPhIdx] = useState(0);
  const [phText, setPhText] = useState(PLACEHOLDERS[0]);
  const inputRef = useRef(null);

  // Cycle placeholders with a typewriter effect
  useEffect(() => {
    let charIdx = 0;
    let currentPh = PLACEHOLDERS[phIdx];
    setPhText('');
    const typing = setInterval(() => {
      if (charIdx < currentPh.length) {
        setPhText(currentPh.slice(0, charIdx + 1));
        charIdx += 1;
      } else {
        clearInterval(typing);
        setTimeout(() => setPhIdx((i) => (i + 1) % PLACEHOLDERS.length), 3500);
      }
    }, 32);
    return () => clearInterval(typing);
  }, [phIdx]);

  const startGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/build';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitPrompt = () => {
    if (!prompt.trim()) {
      inputRef.current?.focus();
      return;
    }
    // Stash the prompt so /build can pick it up (or redirect to auth first)
    try {
      sessionStorage.setItem('atw:initialPrompt', prompt);
    } catch (_e) {
      // ignore storage errors
    }
    startGoogleAuth();
  };

  const onKey = (e) => {
    if (e.key === 'Enter') submitPrompt();
  };

  return (
    <section
      id="top"
      className="relative min-h-screen overflow-hidden text-white"
      style={{ background: 'radial-gradient(ellipse at center, #050510 0%, #030303 60%, #000 100%)' }}
    >
      {/* 3D globe absolutely anchored — visible behind headline */}
      <div className="absolute inset-0 flex items-center justify-center opacity-90 pointer-events-none">
        <div style={{ transform: 'translateY(6%)' }}>
          <InteractiveGlobe size={820} />
        </div>
      </div>

      {/* Radial cyan/magenta blooms */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(600px 400px at 15% 20%, rgba(0,240,255,0.18) 0%, transparent 60%),' +
            'radial-gradient(600px 400px at 85% 80%, rgba(255,0,127,0.16) 0%, transparent 60%)',
        }}
      />

      {/* Content wrapper */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 lg:pt-40 pb-20">
        {/* Top mono kicker */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center mb-6"
        >
          <div className="glass rounded-full px-4 py-2 flex items-center gap-2.5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#00F0FF] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00F0FF]" />
            </span>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/85">
              atw-01 · session live · 12,437 builds this week
            </span>
          </div>
        </motion.div>

        {/* MASSIVE GIANT SHINY GLASSY AROUND THE WORLD AI WORDMARK */}
        <motion.div
          initial={{ opacity: 0, y: 30, filter: 'blur(20px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 1.1, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-10 relative"
        >
          {/* Radial glow behind wordmark */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(600px 200px at 50% 50%, rgba(0,240,255,0.28) 0%, rgba(255,0,127,0.15) 45%, transparent 75%)',
              filter: 'blur(25px)',
            }}
          />
          <div className="relative inline-flex flex-col items-center">
            {/* Glass plate behind text */}
            <div className="relative">
              <h2
                className="font-display font-black tracking-[-0.05em] uppercase leading-[0.85] text-[42px] sm:text-[64px] md:text-[84px] lg:text-[104px]"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(226,232,240,0.98) 35%, rgba(0,240,255,0.85) 55%, rgba(255,0,127,0.85) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow:
                    '0 0 60px rgba(0,240,255,0.35), 0 0 100px rgba(255,0,127,0.25)',
                  filter: 'drop-shadow(0 6px 40px rgba(0,240,255,0.25))',
                }}
              >
                Around The World
              </h2>
              <div className="mt-1 flex items-center justify-center gap-3 md:gap-4">
                <span
                  className="h-px w-16 md:w-24 rounded-full"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(0,240,255,0.9), transparent)',
                  }}
                />
                <span
                  className="font-display font-black text-[42px] sm:text-[56px] md:text-[72px] lg:text-[88px] tracking-[-0.02em] leading-none"
                  style={{
                    background:
                      'linear-gradient(90deg, #00F0FF 0%, #ffffff 50%, #FF007F 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    filter: 'drop-shadow(0 0 30px rgba(255,0,127,0.4))',
                  }}
                >
                  .AI
                </span>
                <span
                  className="h-px w-16 md:w-24 rounded-full"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(255,0,127,0.9), transparent)',
                  }}
                />
              </div>
              {/* Glassy transparent underline strip */}
              <div className="mx-auto mt-3 max-w-md h-[1.5px] rounded-full opacity-70"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, #00F0FF 30%, #FF007F 70%, transparent 100%)',
                }}
              />
              <div className="mt-4 font-mono uppercase tracking-[0.45em] text-[9px] md:text-[11px] text-white/50">
                the ai dev studio for founders who ship
              </div>
            </div>
          </div>
        </motion.div>

        {/* MEGA HEADLINE */}
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-center text-4xl sm:text-6xl lg:text-[5rem] font-black tracking-[-0.045em] leading-[0.95] max-w-5xl mx-auto"
        >
          <span className="block liquid-chrome">Describe an app.</span>
          <span className="block mt-2">
            <span className="italic font-black bg-clip-text text-transparent bg-gradient-to-r from-[#00F0FF] via-white to-[#FF007F]">
              Ship a company.
            </span>
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 text-center max-w-2xl mx-auto text-white/70 text-lg md:text-xl leading-relaxed"
        >
          The AI dev studio for founders who ship. One conversation. A full-stack app.
          Deployed globally in minutes.
        </motion.p>

        {/* PROMPT-FIRST GIANT INPUT */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 relative max-w-4xl mx-auto"
        >
          <div className="hero-prompt glass-heavy rounded-3xl px-6 py-5 md:px-8 md:py-6 relative overflow-hidden">
            {/* Corner glows */}
            <div
              className="absolute -top-24 -left-24 w-64 h-64 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(0,240,255,0.35), transparent 60%)', filter: 'blur(30px)' }}
            />
            <div
              className="absolute -bottom-24 -right-24 w-64 h-64 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(255,0,127,0.3), transparent 60%)', filter: 'blur(30px)' }}
            />

            <div className="relative flex items-center gap-4">
              {/* Prompt icon */}
              <div className="hidden sm:flex h-11 w-11 rounded-xl bg-white/5 border border-white/10 items-center justify-center flex-shrink-0">
                <Sparkles size={16} className="text-[#00F0FF]" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1.5">
                  Prompt · Claude 4.5
                </div>
                <input
                  ref={inputRef}
                  data-testid="hero-prompt-input"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={phText}
                  className="font-mono w-full bg-transparent border-none outline-none text-white text-base md:text-lg placeholder:text-white/25"
                />
              </div>

              {/* Submit */}
              <button
                data-testid="hero-prompt-submit"
                onClick={submitPrompt}
                className="magnetic-btn h-12 md:h-13 px-5 md:px-6 rounded-2xl bg-gradient-to-r from-[#00F0FF] via-white to-[#FF007F] text-black font-black text-sm md:text-base flex items-center gap-2 shadow-[0_10px_40px_-10px_rgba(0,240,255,0.6)]"
              >
                <span className="hidden md:inline">Ship it</span>
                <ArrowRight size={18} strokeWidth={2.5} />
              </button>
            </div>

            {/* Keyboard hint + quick starters */}
            <div className="mt-4 pt-4 border-t border-white/8 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                Try:
              </span>
              {[
                'A blog for chefs',
                'Fitness tracker',
                'AI legal assistant',
                'NFT marketplace',
              ].map((s) => (
                <button
                  key={s}
                  data-testid={`hero-chip-${s.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => setPrompt(s)}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/75 hover:bg-white/10 hover:text-white transition"
                >
                  {s}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-1 text-[10px] text-white/40 font-mono">
                <Command size={11} />
                <span>+ K to focus</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Alternate CTAs */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-6 flex items-center justify-center gap-4"
        >
          <button
            data-testid="hero-google-signin"
            onClick={startGoogleAuth}
            className="magnetic-btn text-sm font-medium text-white/80 hover:text-white flex items-center gap-2"
          >
            <GoogleIcon size={16} />
            Continue with Google
          </button>
          <span className="text-white/25">·</span>
          <button
            data-testid="hero-watch-demo"
            onClick={onSignIn}
            className="magnetic-btn text-sm font-medium text-white/80 hover:text-white flex items-center gap-2"
          >
            <Play size={12} fill="currentColor" />
            Watch 90-sec film
          </button>
        </motion.div>

        {/* Bottom marquee of AI models */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="mt-20 md:mt-28 relative"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4 text-center">
            Powered by the world&rsquo;s frontier AI &mdash; auto-routed per task
          </div>
          <div
            className="relative py-4 border-y border-white/8"
            style={{
              maskImage:
                'linear-gradient(90deg, transparent, black 15%, black 85%, transparent)',
              WebkitMaskImage:
                'linear-gradient(90deg, transparent, black 15%, black 85%, transparent)',
            }}
          >
            <Marquee gradient={false} speed={30} pauseOnHover>
              {[
                'anthropic', 'openai', 'gemini', 'stripe', 'mongodb',
                'react', 'fastapi', 'vercel', 'cloudflare', 'github',
                'tailwindcss', 'python', 'typescript', 'elevenlabs', 'google',
              ].map((id, i) => (
                <div key={i} className="mx-10 flex items-center">
                  <OfficialLogo id={id} height={26} />
                  <span className="ml-6 h-1 w-1 rounded-full bg-white/20" />
                </div>
              ))}
            </Marquee>
          </div>
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none bg-gradient-to-b from-transparent to-black" />
    </section>
  );
}
